import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Button, Col, Divider, Form, message, Row } from 'antd';
import { useParams } from 'react-router';
import CcxComponentProps from '../../../../core/CcxComponent';
import ProvisionService from '../../../../services/ProvisionService';
import styles from './DataStoreSettings.module.less';
import DataStoreSettingsMaintenance from './DataStoreSettingsMaintenance';
import DataStoreSettingsNameChange from './DataStoreSettingsNameChange';
import DeploymentsItem from '../../../../types/DeploymentsItem';
import useBackupSettings from '../../../../core/hooks/useBackupSettings';
import BackupService from '../../../../services/BackupService';
import useDataStoreJobs from '../../../../core/hooks/useDataStoreJobs';
import useDataStore from '../../../../core/hooks/useDataStore';
import { getNonErroredJobs } from '../../../../core/helpers';
import LazyLoader from '../../../LazyLoader';
import DatastoreSettingsAuthenticationTable from './DatastoreSettingsAuthenticationTable';
import DatastoreEmailNotification from './DatastoreEmailNotification';
import DataStoreSettingsAutoScale from './DataStoreSettingsAutoScale';
import {
    AUTOSCALE_STORAGE_PERCENTAGE,
    AUTOSCALE_STORAGE_THRESHOLD,
} from '../../../../core/CcxEnv';
import debounce from 'debounce';

interface UrlProps {
    dataStoreUuid: string;
}

interface Props extends CcxComponentProps {
    currentDeployment?: DeploymentsItem | null | undefined;
}

function DataStoreSettingsGeneral({
    currentDeployment,
    testId = 'DataStoreSettingsGeneral',
}: Props): ReactElement {
    const [form] = Form.useForm();
    const { dataStoreUuid } = useParams<UrlProps>();
    const [dataStoreName, setDataStoreName] = useState<string | undefined>(
        undefined
    );
    const [maintenanceDayOfWeek, setMaintenanceDayOfWeek] = useState<
        number | undefined
    >(undefined);
    const [maintenanceStartHour, setMaintenanceStartHour] = useState<
        number | undefined
    >(undefined);

    const [newDataStoreName, setNewDataStoreName] = useState<
        string | undefined
    >(undefined);

    const [autoscaleStorage, setAutoscaleStorage] = useState<
        boolean | undefined
    >(undefined);

    const [newDayOfWeek, setNewDayOfWeek] = useState<number | undefined>(
        undefined
    );
    const [newStartHour, setNewStartHour] = useState<number | undefined>(
        undefined
    );
    const [newEndHour, setNewEndHour] = useState<number | undefined>(undefined);
    const [newPassword, setNewPassword] = useState<string | undefined>(
        undefined
    );
    const [newPasswordConfirm, setNewPasswordConfirm] = useState<
        string | undefined
    >(undefined);

    const [saving, setSaving] = useState(false);
    const [canSave, setCanSave] = useState(false);
    const [canSaveBackup, setCanSaveBackup] = useState(false);
    const [emailsList, setEmailsList] = useState<string[] | undefined>([]);
    const [full, setFull] = useState<boolean | undefined>();
    const [incremental, setIncremental] = useState<boolean | undefined>();
    const [frequency, setFrequency] = useState<number | undefined>();
    const [time, setTime] = useState<number | undefined>();

    const {
        jobs,
        refresh: refreshJobs,
        loading: jobsLoading,
    } = useDataStoreJobs({
        dataStoreUuid,
    });

    const { dataStore: deployment } = useDataStore(dataStoreUuid);

    const { backupSettings, refresh: refreshBackupSettings } =
        useBackupSettings(dataStoreUuid);

    useEffect(() => {
        if (backupSettings) {
            setIncremental((backupSettings.incFrequency || 0) > 0);

            setFull((backupSettings.fullFrequency || 0) > 0);

            if (backupSettings.fullFrequency === 1) {
                setFrequency(backupSettings.fullFrequency);
            } else if (backupSettings.fullFrequency === 2) {
                setFrequency(backupSettings.fullFrequency);
                setTime(backupSettings.fullStartHour);
            }
        }
    }, [backupSettings]);

    useEffect(() => {
        if (currentDeployment) {
            setDataStoreName(currentDeployment.getName());
            setAutoscaleStorage(currentDeployment.getAutoscaleStorage());
            setMaintenanceDayOfWeek(
                currentDeployment.getMaintenanceDayOfWeek()
            );
            setMaintenanceStartHour(
                currentDeployment.getMaintenanceStartHour()
            );
            setEmailsList(currentDeployment.getNotificationsEmailList());

            form.setFieldsValue({
                maintenanceStartHour:
                    currentDeployment.getMaintenanceStartHour(),
                maintenanceDayOfWeek:
                    currentDeployment.getMaintenanceDayOfWeek(),
                notificationRecipients:
                    currentDeployment.getNotificationsEmailList(),
                autoscaleStorage: currentDeployment.getAutoscaleStorage(),
            });
        }
    }, []);

    const saveDataStoreName = (name: string) => {
        if (name && name !== dataStoreName) {
            setNewDataStoreName(name);
            saveDataStoreSettings();
        }
    };

    const saveDataStoreSettings = useCallback(
        debounce(() => {
            setCanSave(true);
        }, 1000),
        []
    );

    const saveDayOfWeek = (dayOfWeek: number) => {
        if (dayOfWeek) setNewDayOfWeek(dayOfWeek);
        saveDataStoreSettings();
    };

    const saveTimeWindow = (startHour: number, endHour: number) => {
        if (startHour >= 0) {
            setNewStartHour(startHour);
            setNewEndHour(endHour);
        }
        saveDataStoreSettings();
    };

    const saveAutoscaleStorage = (value: boolean) => {
        if (value !== autoscaleStorage) setAutoscaleStorage(value);
        saveDataStoreSettings();
    };

    const saveEmailNotifications = (emails: string[]) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValidEmail = emails.every((email: string) => {
            return emailRegex.test(email);
        });
        setEmailsList(emails);
        if (!isValidEmail) {
            setCanSave(false);
        } else {
            saveDataStoreSettings();
        }
    };

    const handleSettingsChanged = async () => {
        try {
            if (newDataStoreName) await form.validateFields(['dataStoreName']);
            if (newDayOfWeek)
                await form.validateFields(['maintenanceDayOfWeek']);
            if (newStartHour || newEndHour)
                await form.validateFields(['maintenanceStartHour']);
            if (newPassword || newPasswordConfirm)
                await form.validateFields(['password', 'confirmPassword']);
        } catch (e) {
            message.error(
                'One or more settings are invalid. Please double check and try again.'
            );
            return;
        }

        setSaving(true);

        if (canSave) {
            try {
                await ProvisionService.updateDeployment({
                    dataStoreUuid,
                    dataStoreName: newDataStoreName?.trim(),
                    dayOfWeek:
                        newDayOfWeek ||
                        form.getFieldValue('maintenanceDayOfWeek'),
                    startHour:
                        newStartHour ||
                        form.getFieldValue('maintenanceStartHour'),
                    endHour:
                        newEndHour ||
                        (form.getFieldValue('maintenanceStartHour') + 2) % 24,
                    proxySqlAdminPassword:
                        newPassword === newPasswordConfirm
                            ? newPassword
                            : undefined,
                    autoscaleStorage:
                        autoscaleStorage ||
                        form.getFieldValue('autoscaleStorage'),
                    notifications: {
                        enabled: emailsList ? emailsList.length > 0 : false,
                        emails: emailsList || [],
                    },
                });
                message.success('Your datastore settings have been updated.');
                setCanSave(false);
                setSaving(false);
                setNewDataStoreName(undefined);
                setNewDayOfWeek(undefined);
                setNewStartHour(undefined);
                setNewEndHour(undefined);
                setNewPassword(undefined);
                setNewPasswordConfirm(undefined);
            } catch (e) {
                message.error(
                    'There was an error updating your datastore settings. Please try again.'
                );
                setSaving(false);
                return;
            }
        }

        if (canSaveBackup) {
            try {
                const settingsObj = {
                    full_frequency: full ? frequency : 0,
                    full_start_hour: frequency === 2 ? time : undefined,
                    inc_frequency: incremental ? 1 : 0,
                };

                await BackupService.updateBackupSettings(
                    dataStoreUuid,
                    settingsObj
                );
                refreshBackupSettings && (await refreshBackupSettings());
                message.success('Your backup settings have been updated.');
                setCanSaveBackup(false);
                setSaving(false);
            } catch (e) {
                message.error(
                    'There was an error updating your backup settings. Please try again.'
                );
                setSaving(false);
                return;
            }
        }
    };

    useEffect(() => {
        if (canSave) {
            handleSettingsChanged();
        }
    }, [canSave]);

    const handleUpgradeTo = async () => {
        if (currentDeployment?.can_upgrade_to) {
            try {
                await ProvisionService.upgradeDatastore({
                    dataStoreUuid,
                    canUpgradeTo: currentDeployment?.can_upgrade_to,
                });

                refreshJobs();

                message.info('Your datastore is being upgraded.');
            } catch (error) {
                message.error(
                    'There was an error upgrading your datastore. Please try again.'
                );
                return;
            }
        }
    };

    return jobsLoading && !currentDeployment ? (
        <LazyLoader type="row" />
    ) : (
        <div className={styles.DataStoreSettings} data-testid={testId}>
            <DataStoreSettingsNameChange
                dataStoreName={dataStoreName}
                form={form}
                onChange={saveDataStoreName}
            />

            <Divider />

            <DataStoreSettingsMaintenance
                currentDeployment={currentDeployment}
                form={form}
                maintenanceDayOfWeek={maintenanceDayOfWeek}
                maintenanceStartHour={maintenanceStartHour}
                onDayOfWeekChange={saveDayOfWeek}
                onTimeWindowChange={saveTimeWindow}
                canUpgradeTo={currentDeployment?.can_upgrade_to}
                packageUpdateCandidates={deployment?.getPackageUpdateCandidates()}
                handleUpgradeTo={handleUpgradeTo}
                filteredJob={getNonErroredJobs(jobs)}
            />

            <Divider />

            {!currentDeployment?.isRedis() &&
            !currentDeployment?.isEphemeral() &&
            AUTOSCALE_STORAGE_THRESHOLD != 0 &&
            AUTOSCALE_STORAGE_PERCENTAGE != 0 ? (
                <>
                    <DataStoreSettingsAutoScale
                        autoscaleStorage={autoscaleStorage}
                        onChange={saveAutoscaleStorage}
                        form={form}
                    />

                    <Divider />
                </>
            ) : (
                <></>
            )}

            <DatastoreEmailNotification
                form={form}
                emailsList={emailsList || []}
                saveEmailNotifications={saveEmailNotifications}
            />

            {currentDeployment?.isCAAvailable && (
                <>
                    <Divider />
                    <DatastoreSettingsAuthenticationTable />
                </>
            )}
        </div>
    );
}

export default DataStoreSettingsGeneral;
